import React, { useEffect, useState } from 'react';
import {
  SuperList,
  DisabledTag,
  getTranslatedText,
  ALL_TABLES,
  getDefaultValue,
  useSuperListHook,
  CurrencyText
} from '@nile/components';
import {
  AccountClient,
  GET_RETAINER_SUMMARY_LIST,
  GET_CLIENTS
} from '@nile/graphql';
import {
  TextStyle,
  Link,
  DisplayText,
  Button
} from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';

const DEFAULT_ROW_COUNT = 20;

const defaultCondition = {
  joinOperator: 'AND',
  operands: [
    {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'clientContractInfo.contractStatus',
          operator: 'is',
          value: 'ACTIVE'
        }
      ]
    }
  ]
};

const RetainerSummaryReportTable = ({
  reportId,
  handleClientClick
}: {
  reportId: string;
  handleClientClick: (
    client: { accountId: string; name: string },
    contract?: {
      contractId: string | null;
      contractName?: string;
      contractType?: string | null;
    }
  ) => void;
}) => {
  const intl = useIntl();

  const {
    records,
    setRecords,
    tableCondition,
    updateTableFilterCondition,
    showResetFilter,
    resetFilters
  } = useSuperListHook({
    tableName: ALL_TABLES.retainerSummaryReportTable,
    viewId: reportId
  });

  const [data, setData] = useState([]);

  const columns = [
    {
      Header: getTranslatedText(intl, 'common.client'),
      accessor: 'client.name'
    },
    {
      Header: getTranslatedText(intl, 'common.contract'),
      accessor: 'contractName'
    },
    {
      Header: getTranslatedText(
        intl,
        'invoice.retainerReport.table.header.offeringType'
      ),
      accessor: 'offeringType'
    },
    {
      Header: getTranslatedText(
        intl,
        'invoice.retainerReport.table.header.consumption'
      ),
      accessor: 'consumed'
    },
    {
      Header: getTranslatedText(
        intl,
        'invoice.retainerReport.table.header.allotted'
      ),
      accessor: 'totalAllotted'
    },
    {
      Header: getTranslatedText(
        intl,
        'invoice.retainerReport.table.header.remaining'
      ),
      accessor: 'remaining'
    },
    {
      Header: getTranslatedText(
        intl,
        'invoice.retainerReport.table.header.utilization'
      ),
      accessor: 'utilization'
    }
  ];

  const generateOptions = (col: any, clientData: any) => {
    switch (col?.name) {
      case 'client.name':
        return clientData?.getClientList?.clients?.map((el: any) => {
          return {
            ...el,
            name: el.accountId,
            value: el.name,
            label: el.name
          };
        });

      case 'offeringType':
        return clientData;
      default:
        return null;
    }
  };

  const getDynamicQuery = (col: any) => {
    switch (col?.name) {
      case 'client.name':
        return AccountClient.query({
          query: GET_CLIENTS,
          variables: {
            listInfo: {
              sort: {
                attribute: 'name',
                order: 'ASC'
              }
            }
          }
        });
      case 'offeringType':
        return {
          data: [
            {
              label: getTranslatedText(intl, 'admin.serviceCatalog.blockHour'),
              value: 'BLOCK_HOUR'
            },
            {
              label: getTranslatedText(intl, 'admin.serviceCatalog.blockMoney'),
              value: 'BLOCK_MONEY'
            }
          ]
        };
      default:
        return null;
    }
  };

  const offeringTypeMap = {
    BLOCK_HOUR: 'admin.serviceCatalog.blockHour',
    BLOCK_MONEY: 'admin.serviceCatalog.blockMoney'
  };

  const checkDynamicQuery = (col: any) => {
    switch (col?.name) {
      case 'client.name':
      case 'offeringType':
        return true;
      default:
        return false;
    }
  };

  const getFilterType = (column: any) => {
    switch (column.accessor) {
      case 'client.name':
      case 'offeringType':
        return 'select';
      default:
        return 'text';
    }
  };

  const getDataType = (column: any) => {
    switch (column.accessor) {
      default:
        return 'string';
    }
  };

  const [columnGroup, setColumnGroup] = useState<any[]>([]);

  const DynamicCell = ({ cell }: { cell: any }) => {
    const { actions, editable } = cell?.column;
    const rowData = cell?.row?.original;
    const isEditable = editable && rowData?.status !== 'INACTIVE';

    switch (cell.column.accessor) {
      case 'action':
        return (
          <Link
            onClick={() => actions.onClick(rowData)}
            className="cursor-pointer underline f-12"
            role="button"
            tabIndex={cell?.row?.index || 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <FormattedMessage id="common.view.details" />
          </Link>
        );
      case 'offeringType':
        return cell?.value ? (
          <div className="fw">
            <FormattedMessage
              id={
                offeringTypeMap[cell?.value as keyof typeof offeringTypeMap] ||
                cell?.value
              }
            />
          </div>
        ) : (
          <DisabledTag>
            <FormattedMessage id="app.noData.message" />
          </DisabledTag>
        );
      case 'consumed':
      case 'totalAllotted':
      case 'remaining':
        return rowData?.offeringType === 'BLOCK_HOUR' ? (
          <div className="fw">{`${cell?.value ?? '0'} hr`}</div>
        ) : (
          <CurrencyText value={cell?.value ?? 0} />
        );
      case 'utilization':
        return (
          <div className="fw">{cell?.value ? `${cell?.value} %` : '0%'}</div>
        );
      default:
        return cell?.value ? (
          <div>{cell?.value}</div>
        ) : (
          <DisabledTag>
            <FormattedMessage id="app.noData.message" />
          </DisabledTag>
        );
    }
  };

  const defaultColumnCells = {
    DynamicCell: DynamicCell
  };

  const getActions = (col: any) => {
    return {
      onClick: (item: any) => {
        handleClientClick(
          item.client,
          item.contractId
            ? {
                contractId: item.contractId,
                contractName: item.contractName
              }
            : undefined
        );
      }
    };
  };

  function getSortBy(column: any) {
    switch (column.accessor) {
      case 'client.name':
      case 'contractName':
      case 'offeringType':
        return true;
      default:
        return false;
    }
  }

  function getFilterBy(column: any) {
    switch (column.accessor) {
      case 'client.name':
      case 'contractName':
      case 'offeringType':
        return true;
      default:
        return false;
    }
  }

  function generateColumns(): void {
    const columnsGrp = columns.map((col) => {
      return {
        name: col.accessor,
        Header: col.Header,
        header: col.Header,
        accessor: col.accessor,
        accessorKey: col.accessor,
        id: col.accessor,
        size: 100,
        disableResizing: false,
        disableSortBy: !getSortBy(col),
        editable: false,
        disableFilters: !getFilterBy(col),
        filterType: getFilterType(col),
        dataType: getDataType(col),
        type: 'DynamicCell',
        actions: getActions(col),
        field: {
          name: col.accessor,
          accessor: col.accessor,
          label: col.Header,
          sortable: false,
          id: col.accessor,
          dataType: getDataType(col),
          filterType: getFilterType(col)
        },
        helpers: {
          getDefaultValue,
          updateConditions: updateTableFilterCondition,
          checkDynamicQuery,
          getDynamicQuery,
          generateOptions
        }
      };
    });
    columnsGrp.push({
      Header: ' ',
      header: ' ',
      name: ' ',
      accessorKey: 'action',
      accessor: 'action',
      size: 70,
      id: 'action',
      disableResizing: true,
      type: 'DynamicCell',
      disableSortBy: true,
      editable: false,
      disableFilters: true,
      actions: getActions({ accessor: 'action' }),
      filterType: '',
      dataType: '',
      field: {
        name: 'action',
        accessor: 'action',
        label: 'Action',
        sortable: false,
        id: 'action',
        dataType: '',
        filterType: ''
      },
      helpers: {
        getDefaultValue,
        updateConditions: updateTableFilterCondition,
        checkDynamicQuery,
        getDynamicQuery,
        generateOptions
      }
    });
    setColumnGroup(columnsGrp);
  }

  useEffect(() => {
    generateColumns();
  }, []);

  function checkBoxDisabledCheck(rowData: any) {
    if (!records?.length) {
      return false;
    }
    if (rowData?.isGroupHeader) {
      return !rowData?.subRows?.some(
        (subRow: any) =>
          subRow?.client?.accountId === records[0]?.client?.accountId
      );
    }
    return rowData?.client?.accountId !== records[0]?.client?.accountId;
  }

  return (
    <div className="full-width full-height">
      <div className="fx actr jctr position-sticky z400 white-to-grey-bg">
        <div className="grid fw actr mb10 p20">
          <DisplayText size="extraLarge" weight="SemiBold">
            <FormattedMessage
              id="common.label.contractsUtilization"
              defaultMessage="Retainer Summary Report"
            />
          </DisplayText>
          <TextStyle variation="subdued">
            <FormattedMessage
              id="common.label.retainerSummaryReport"
              defaultMessage="Overview of all active retainers and their current utilization status"
            />
          </TextStyle>
        </div>
        <div className="mlauto w100p">
          {showResetFilter && (
            <Button id="resetSuperListFilter" plain onClick={resetFilters}>
              <FormattedMessage id="ticket.details.resetFilters" />
            </Button>
          )}
        </div>
      </div>
      <div
        className={`min-height-300 ${data?.length === 0 ? 'height300' : ''}`}
      >
        <SuperList
          padded="sm"
          data={data}
          headerSticky
          stickyTop={94}
          records={records}
          setRecords={setRecords}
          viewId={reportId}
          setData={setData}
          columns={columnGroup}
          rowCount={data?.length}
          defaultColumnCells={defaultColumnCells}
          defaultRowCount={DEFAULT_ROW_COUNT}
          emptyState={{
            nodataImage: `${envConfig.imageRoot}svg/not-found.svg`,
            heading: getTranslatedText(
              intl,
              'admin.mandatoryTicketFields.list.noData.heading'
            ),
            description: getTranslatedText(
              intl,
              'admin.mandatoryTicketFields.list.noData.description'
            )
          }}
          defaultSort={[
            {
              attribute: 'client.name',
              order: 'ASC'
            }
          ]}
          groupAccessor={(rowItem: any) => ({
            id: rowItem?.client?.accountId,
            name: rowItem?.client?.name ? (
              <div className="fx center g10">
                <SVGIcon name="ClientLogo" />
                <span className="cw-20">
                  <Link onClick={() => handleClientClick(rowItem.client)}>
                    {rowItem?.client?.name}
                  </Link>
                </span>
              </div>
            ) : null,
            empty: getTranslatedText(intl, 'app.noData.message')
          })}
          isGroupingEnabled
          defaultExpanded
          disableSelectAll
          maintainSelection
          idKey="contractId"
          tableName={ALL_TABLES.retainerSummaryReportTable}
          client={AccountClient}
          query={GET_RETAINER_SUMMARY_LIST}
          accessorKey="getRetainerSummaryList"
          dataKey="retainerUtilizations"
          defaultCondition={defaultCondition}
          tableCondition={tableCondition}
          checkBoxDisabledCheck={checkBoxDisabledCheck}
        />
      </div>
    </div>
  );
};

export default RetainerSummaryReportTable;
